<div class="edi-configurations">
  <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-3">
    <h3 class="page-title mb-2 mb-md-0">EDI configurations</h3>
    <button
      type="button"
      class="btn btn-outline-secondary"
      (click)="loadConfigurations()"
      [disabled]="loading || saving"
    >
      <i class="fa fa-refresh mr-1" aria-hidden="true"></i>
      Refresh
    </button>
  </div>

  <div *ngIf="!isActuator" class="alert alert-info py-2" role="alert">
    You can view configurations. Saving requires the ACTUATOR role.
  </div>

  <div *ngIf="loadError" class="alert alert-danger py-2" role="alert">
    {{ loadError }}
  </div>

  <div class="row">
    <div class="col-12 col-xl-4 mb-3 mb-xl-0">
      <div class="edi-config-list border">
        <div class="edi-config-list-header p-3 border-bottom">
          <label for="edi-config-filter" class="small text-muted mb-1">Filter</label>
          <input
            #filterInput
            id="edi-config-filter"
            type="text"
            class="form-control"
            [value]="filterText"
            (input)="onFilterTextChange(filterInput.value)"
            placeholder="ID, instance, server"
          />
        </div>

        <div class="table-responsive edi-config-list-table">
          <table class="table table-hover table-sm mb-0">
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Instance</th>
                <th scope="col">Enabled</th>
                <th scope="col">Protocol</th>
                <th scope="col">Server</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngIf="loading">
                <td colspan="5" class="text-center py-4">
                  <i class="fa fa-spinner fa-spin mr-2" aria-hidden="true"></i>
                  Loading
                </td>
              </tr>
              <tr *ngIf="!loading && filteredConfigurations.length === 0">
                <td colspan="5" class="text-center text-muted py-4">No EDI configurations found.</td>
              </tr>
              <tr
                *ngFor="let configuration of filteredConfigurations; trackBy: trackByConfigurationId"
                class="edi-config-row"
                [ngClass]="{ active: isSelected(configuration) }"
                (click)="selectConfiguration(configuration)"
              >
                <td>
                  <button type="button" class="btn btn-link btn-sm p-0 text-left">
                    {{ configuration.id }}
                  </button>
                </td>
                <td>{{ configuration.instanceName || '-' }}</td>
                <td>
                  <span class="badge" [ngClass]="configuration.enabled ? 'badge-success' : 'badge-secondary'">
                    {{ configuration.enabled ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td>{{ configuration.ftpProtocol || '-' }}</td>
                <td>{{ configuration.ftpServer || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="col-12 col-xl-8">
      <div *ngIf="selectedConfiguration && form; else noSelection" class="edi-config-editor border">
        <form [formGroup]="form" (ngSubmit)="save()">
          <div class="edi-config-editor-header p-3 border-bottom">
            <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
              <div class="mb-2 mb-lg-0">
                <div class="small text-muted">Editing</div>
                <h4 class="mb-0">{{ selectedConfiguration.id }}</h4>
              </div>
              <div class="d-flex align-items-center">
                <span *ngIf="form.dirty" class="badge badge-warning mr-2"> Unsaved </span>
                <button
                  type="submit"
                  class="btn btn-primary"
                  [disabled]="!isActuator || saving || loading || form.invalid || mappingBindingInvalid || !form.dirty"
                >
                  Save
                  <i *ngIf="saving" class="fa fa-spinner fa-spin ml-2" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </div>

          <fieldset [disabled]="saving || loading" class="p-3">
            <div *ngIf="saveError" class="alert alert-danger py-2" role="alert">
              {{ saveError }}
            </div>

            <section *ngIf="usesMappingInstructionTransformer" class="edi-config-section mb-4">
              <h5 class="edi-config-section-title">Incoming order mapping instruction</h5>
              <p class="small text-muted">
                This configuration loads one exact, immutable saved model version. Creating a newer model version does
                not change production mapping until this configuration is updated.
              </p>
              <div *ngIf="!mappingRuntimeAvailable" class="alert alert-warning py-2" role="alert">
                The backend does not report mapping-runtime support. Update the backend tier before using this
                qualifier.
              </div>
              <div *ngIf="mappingModelsError" class="alert alert-danger py-2" role="alert">
                {{ mappingModelsError }}
              </div>
              <div class="row">
                <div class="col-12 col-md-8 mb-3">
                  <label for="edi-config-incoming-mapping-model" class="small text-muted mb-1"
                    >Saved mapping model</label
                  >
                  <select
                    id="edi-config-incoming-mapping-model"
                    class="form-control"
                    formControlName="incomingOrderMappingModelId"
                    (change)="onMappingModelChange($any($event.target).value)"
                    [disabled]="!mappingRuntimeAvailable || mappingModelsLoading"
                  >
                    <option value="">Select an ORDER mapping model</option>
                    <option
                      *ngFor="let model of savedOrderMappingModels; trackBy: trackByMappingModel"
                      [value]="model.modelId"
                    >
                      {{ model.name }} (latest v{{ model.version }})
                    </option>
                  </select>
                </div>
                <div class="col-12 col-md-4 mb-3">
                  <label for="edi-config-incoming-mapping-version" class="small text-muted mb-1">Exact version</label>
                  <select
                    id="edi-config-incoming-mapping-version"
                    class="form-control"
                    formControlName="incomingOrderMappingModelVersion"
                    [disabled]="!mappingRuntimeAvailable || mappingModelsLoading || mappingModelVersions.length === 0"
                  >
                    <option [ngValue]="null">Select version</option>
                    <option
                      *ngFor="let model of mappingModelVersions; trackBy: trackByMappingModel"
                      [value]="model.version"
                    >
                      v{{ model.version }} — {{ model.modifiedBy }}
                    </option>
                  </select>
                </div>
              </div>
              <div *ngIf="mappingBindingInvalid" class="text-danger small">
                Select both a saved ORDER mapping model and an exact version.
              </div>
            </section>

            <section *ngFor="let group of fieldGroups" class="edi-config-section mb-4">
              <h5 class="edi-config-section-title">{{ group.title }}</h5>
              <div class="row">
                <div
                  *ngFor="let field of group.fields; trackBy: trackByFieldKey"
                  class="mb-3"
                  [ngClass]="field.type === 'textarea' || field.type === 'array' ? 'col-12' : 'col-12 col-md-6'"
                >
                  <label
                    *ngIf="field.type !== 'boolean'"
                    [for]="'edi-config-' + field.key"
                    class="small text-muted mb-1"
                  >
                    {{ field.label }}
                  </label>

                  <ng-container [ngSwitch]="field.type">
                    <textarea
                      *ngSwitchCase="'textarea'"
                      [id]="'edi-config-' + field.key"
                      class="form-control"
                      [rows]="field.rows || 2"
                      [formControlName]="field.key"
                    ></textarea>

                    <textarea
                      *ngSwitchCase="'array'"
                      [id]="'edi-config-' + field.key"
                      class="form-control edi-config-array"
                      rows="3"
                      [formControlName]="field.key"
                    ></textarea>

                    <select
                      *ngSwitchCase="'status'"
                      [id]="'edi-config-' + field.key"
                      class="form-control"
                      [formControlName]="field.key"
                    >
                      <option *ngFor="let status of sendStatuses" [value]="status">{{ status }}</option>
                    </select>

                    <div *ngSwitchCase="'boolean'" class="custom-control custom-checkbox edi-config-checkbox">
                      <input
                        type="checkbox"
                        class="custom-control-input"
                        [id]="'edi-config-' + field.key"
                        [formControlName]="field.key"
                      />
                      <label class="custom-control-label" [for]="'edi-config-' + field.key">{{ field.label }}</label>
                    </div>

                    <input
                      *ngSwitchCase="'number'"
                      [id]="'edi-config-' + field.key"
                      type="number"
                      class="form-control"
                      [required]="field.required ? true : null"
                      [formControlName]="field.key"
                    />

                    <input
                      *ngSwitchDefault
                      [id]="'edi-config-' + field.key"
                      type="text"
                      class="form-control"
                      [formControlName]="field.key"
                    />
                  </ng-container>
                </div>
              </div>
            </section>

            <section class="edi-config-section mb-2">
              <h5 class="edi-config-section-title">Sensitive FTP/SFTP fields</h5>
              <div class="row">
                <div *ngFor="let field of sensitiveFields; trackBy: trackByFieldKey" class="col-12 col-md-6 mb-3">
                  <div class="d-flex align-items-center justify-content-between mb-1">
                    <label [for]="'edi-config-' + field.key" class="small text-muted mb-0">{{ field.label }}</label>
                    <span *ngIf="hasExistingSensitiveValue(field.key)" class="badge badge-secondary">Stored</span>
                  </div>
                  <input
                    [id]="'edi-config-' + field.key"
                    type="password"
                    class="form-control"
                    [attr.placeholder]="hasExistingSensitiveValue(field.key) ? 'Stored value preserved' : ''"
                    [readonly]="form.get(getSensitiveControlName(field.key, 'Clear'))?.value"
                    [formControlName]="getSensitiveControlName(field.key, 'NewValue')"
                  />
                  <div class="custom-control custom-checkbox mt-2">
                    <input
                      type="checkbox"
                      class="custom-control-input"
                      [id]="'edi-config-' + field.key + '-clear'"
                      [formControlName]="getSensitiveControlName(field.key, 'Clear')"
                    />
                    <label class="custom-control-label small" [for]="'edi-config-' + field.key + '-clear'">
                      Clear saved value
                    </label>
                  </div>
                </div>
              </div>
            </section>
          </fieldset>
        </form>
      </div>

      <ng-template #noSelection>
        <div class="edi-config-empty border p-4 text-center text-muted">Select an EDI configuration to edit.</div>
      </ng-template>
    </div>
  </div>
</div>
